import { CartLineNode, ShopifyCartItem } from '@/lib/shopify';
import {
  convertCartLineItemPropertiesToAttributes,
  getCartLineItemProperty,
} from '@/utils/shopify';
import { CartItemProperty } from '@/core/constants';
import { normalizeToBoolean } from '@/utils/strings';
import {
  createAttributesMap,
  createGetPropertyFunction,
  calculateDisplayPrice,
  isAddonForParent,
  determineMaximumQuantity,
} from '@/lib/cart/helpers';

export type SerializedCartItemBase = {
  id: string;
  title: string;
  handle: string;
  imageUrl: string;
  price: string;
  currency: string;
  quantity: number;
  subtotal: string;
  subtotalCurrency: string;
  variantTitle: string;
  variantId: string;
  isAddon: boolean;
  maximumQuantity?: number;
  savings?: string;
};

export type SerializedCartItem = SerializedCartItemBase & {
  addons: SerializedCartItemBase[];
};

/**
 * Creates a SerializedCartItemBase from a CartLineNode
 * @param node - The cart line node
 * @param itemPrice - The calculated display price
 * @param parentAttributes - The parent cart item attributes (for addons)
 * @returns SerializedCartItemBase
 */
function createSerializedCartItemBase({
  node,
  itemPrice,
  isAddon,
  parentAttributes,
}: {
  node: CartLineNode;
  itemPrice: { price: string; currency: string };
  isAddon: boolean;
  parentAttributes?: Array<{ key: string; value: string }>;
}): SerializedCartItemBase {
  const attributes = node.attributes || [];
  const attributesMap = createAttributesMap(attributes);
  const getProperty = createGetPropertyFunction(attributesMap);
  const quantityLimitedToOne = getProperty('quantity_limited_to_one');
  const handle = node.merchandise.product?.handle || '';

  const unitPrice = parseFloat(itemPrice.price);
  const subtotal = (unitPrice * node.quantity).toFixed(2);

  return {
    id: node.id,
    title: node.merchandise.product?.title || '',
    handle,
    imageUrl: node.merchandise.image?.url || '',
    price: itemPrice.price,
    currency: itemPrice.currency,
    quantity: node.quantity,
    subtotal,
    subtotalCurrency: node.cost.subtotalAmount.currencyCode,
    variantTitle: node.merchandise.title || '',
    variantId: node.merchandise.id || '',
    maximumQuantity: determineMaximumQuantity(
      handle,
      quantityLimitedToOne,
      parentAttributes || [],
    ),
    isAddon,
  };
}

enum CartProductType {
  PlayKitSubscription = 'Play Kit Subscription',
  PlayKitSubscriptionBookBundleAddon = 'Play Kit Subscription Book Bundle Addon',
  PlayKitGiftSubscription = 'Play Kit Gift Subscription',
  SubscriptionPlan = 'Subscription Plan',
  CoursePack = 'Course Pack',
  Standalone = 'Standalone',
}

export function organizeCartItems(items: ShopifyCartItem[]) {
  type CartItemGroups = {
    playKitSubscriptionProduct: ShopifyCartItem | null;
    playKitSubscriptionBookBundleAddonProduct: ShopifyCartItem | null;
    playKitGiftSubscriptionProduct: ShopifyCartItem | null;
    subscriptionPlanProducts: ShopifyCartItem[];
    coursePackProducts: ShopifyCartItem[];
    standaloneProducts: ShopifyCartItem[];
  };

  const initialGroups: CartItemGroups = {
    playKitSubscriptionProduct: null,
    playKitSubscriptionBookBundleAddonProduct: null,
    playKitGiftSubscriptionProduct: null,
    subscriptionPlanProducts: [],
    coursePackProducts: [],
    standaloneProducts: [],
  };

  return items.reduce((groups, item) => {
    const productType = identifyProductType(item);

    switch (productType) {
      case CartProductType.PlayKitSubscription:
        groups.playKitSubscriptionProduct = item;
        break;

      case CartProductType.PlayKitSubscriptionBookBundleAddon:
        groups.playKitSubscriptionBookBundleAddonProduct = item;
        break;

      case CartProductType.PlayKitGiftSubscription:
        groups.playKitGiftSubscriptionProduct = item;
        break;

      case CartProductType.SubscriptionPlan:
        groups.subscriptionPlanProducts.push(item);
        break;

      case CartProductType.CoursePack:
        groups.coursePackProducts.push(item);
        break;

      case CartProductType.Standalone:
        groups.standaloneProducts.push(item);
        break;
    }

    return groups;
  }, initialGroups);
}

export function identifyProductType(item: ShopifyCartItem): CartProductType {
  const attributes = convertCartLineItemPropertiesToAttributes(item.properties);

  const conditions: [boolean, CartProductType][] = [
    [
      !!getCartLineItemProperty(
        CartItemProperty.PlayKitSubscriptionSettings,
        attributes,
      ),
      CartProductType.PlayKitSubscription,
    ],
    [
      !!getCartLineItemProperty(
        CartItemProperty.PlayKitGiftSubscriptionSettings,
        attributes,
      ),
      CartProductType.PlayKitGiftSubscription,
    ],
    [
      !!getCartLineItemProperty(
        CartItemProperty.SubscriptionPlanSettings,
        attributes,
      ),
      CartProductType.SubscriptionPlan,
    ],
    [
      normalizeToBoolean(
        getCartLineItemProperty(
          CartItemProperty.IsPlayKitSubscriptionBookBundleAddon,
          attributes,
        ),
      ),
      CartProductType.PlayKitSubscriptionBookBundleAddon,
    ],
    [
      normalizeToBoolean(
        getCartLineItemProperty(CartItemProperty.IsCoursePack, attributes),
      ),
      CartProductType.CoursePack,
    ],
  ];

  for (const [identifyingCondition, productType] of conditions) {
    if (identifyingCondition) {
      return productType;
    }
  }

  return CartProductType.Standalone;
}

/**
 * Serializes cart line edges into a friendly array of cart item objects with addons grouped.
 * @param {Array<{ node: CartLineNode }>} edges - The cart line edges from the cart response.
 * @returns {Array<SerializedCartItem>} - Friendly cart items with addons.
 */
export function serializeCartLines(
  edges: { node: CartLineNode }[],
): SerializedCartItem[] {
  // Create a Map for O(1) node lookups by ID
  const nodeMap = new Map<string, CartLineNode>();
  edges.forEach(({ node }) => {
    nodeMap.set(node.id, node);
  });

  const addonItems = new Set<string>();
  const parentToAddons = new Map<string, string[]>();
  const assignedAddons = new Set<string>(); // Track which addons have been assigned

  // Pre-process attributes for all nodes to avoid repeated find() operations
  const nodeAttributesMap = new Map<string, Map<string, string>>();
  edges.forEach(({ node }) => {
    const attributes = node.attributes || [];
    nodeAttributesMap.set(node.id, createAttributesMap(attributes));
  });

  // Pre-process parent references to eliminate O(n²) operations
  const parentReferences = new Map<string, Set<string>>();
  const legacyReferences = new Map<string, Set<string>>();

  edges.forEach(({ node }) => {
    const attributesMap = nodeAttributesMap.get(node.id);
    if (!attributesMap) return;

    const getProperty = createGetPropertyFunction(attributesMap);
    const parentProductId = getProperty('parent_product_id');
    const linkedProductHandle = getProperty('linked_product_handle');

    if (parentProductId) {
      if (!parentReferences.has(parentProductId)) {
        parentReferences.set(parentProductId, new Set());
      }
      const parentRef = parentReferences.get(parentProductId);
      if (parentRef) {
        parentRef.add(node.id);
      }
    }

    if (linkedProductHandle) {
      if (!legacyReferences.has(linkedProductHandle)) {
        legacyReferences.set(linkedProductHandle, new Set());
      }
      const legacyRef = legacyReferences.get(linkedProductHandle);
      if (legacyRef) {
        legacyRef.add(node.id);
      }
    }
  });

  // Identify potential parents and link their addons
  edges.forEach(({ node }) => {
    const attributesMap = nodeAttributesMap.get(node.id);
    if (!attributesMap) return;

    // Check if this is a potential parent (has addons)
    const getProperty = createGetPropertyFunction(attributesMap);
    const isCoursePack = getProperty('is_course_pack') === 'true';
    const isSubscription = getProperty('is_playgenius_subscription') === 'true';

    // O(1) lookup instead of O(n) some() operations
    const hasExplicitParentReference = parentReferences.has(
      node.merchandise.id,
    );
    const hasLegacyParentReference = legacyReferences.has(
      node.merchandise.product?.handle || '',
    );

    // Use explicit parent reference as primary, fallback to legacy methods
    const hasAddons = hasExplicitParentReference || hasLegacyParentReference;

    if (isCoursePack || isSubscription || hasAddons) {
      // Find all potential addons for this parent
      edges.forEach(({ node: potentialAddon }) => {
        // Skip if this addon has already been assigned to another parent
        if (assignedAddons.has(potentialAddon.id)) return;

        if (isAddonForParent(potentialAddon, node, edges)) {
          addonItems.add(potentialAddon.id);
          assignedAddons.add(potentialAddon.id); // Mark as assigned
          if (!parentToAddons.has(node.id)) {
            parentToAddons.set(node.id, []);
          }
          const addons = parentToAddons.get(node.id);
          if (addons) {
            addons.push(potentialAddon.id);
          }
        }
      });
    }
  });

  // Create the serialized items with addons
  const result: SerializedCartItem[] = [];

  for (const { node } of edges) {
    // Skip addon items from main list
    if (addonItems.has(node.id)) continue;

    const addonIds = parentToAddons.get(node.id) || [];
    const addons = [];

    // Process addons
    for (const addonId of addonIds) {
      const addonNode = nodeMap.get(addonId);
      if (!addonNode) continue;

      const addonPrice = calculateDisplayPrice(addonNode);
      addons.push(
        createSerializedCartItemBase({
          node: addonNode,
          itemPrice: addonPrice,
          isAddon: true,
          parentAttributes: node.attributes || [],
        }),
      );
    }

    const itemPrice = calculateDisplayPrice(node);

    result.push({
      ...createSerializedCartItemBase({
        node,
        itemPrice,
        isAddon: false,
      }),
      addons,
      savings: itemPrice.savings,
    });
  }

  return result;
}
